import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IMG2IMG_PROMPT_MAX_LENGTH } from '@/pages/ipe/workbench/constants/img2imgStudioConfig';
import useStudioSidebarDockLeft from '@/pages/ipe/workbench/hooks/useStudioSidebarDockLeft';
import { useHistoryPanelTransition } from '@/pages/ipe/workbench/hooks/useHistoryPanelTransition';
import { resolveLockedTitlePresetScope } from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import '@/styles/ipe/workbench/modals/Text2ImgPromptCaseFormModal.css';

function emptyForm(scope = 'personal', defaults = {}) {
  return {
    name: String(defaults.name || ''),
    prompt: String(defaults.prompt || ''),
    caseScope: scope === 'team' ? 'team' : 'personal',
  };
}

function formFromCase(item, fallbackScope = 'personal') {
  if (!item) return emptyForm(fallbackScope);
  return {
    name: String(item.name || ''),
    prompt: String(item.prompt || ''),
    caseScope: item.scope === 'team' ? 'team' : 'personal',
  };
}

export function validateImg2ImgPromptCaseForm(form, t) {
  const tr = typeof t === 'function' ? t : (key, opts) => (opts?.defaultValue ?? key);
  const name = String(form?.name || '').trim();
  const prompt = String(form?.prompt || '').trim();
  if (!name) return tr('promptCase.errNameRequired');
  if (!prompt) return tr('promptCase.errI2iPromptRequired');
  if (prompt.length > IMG2IMG_PROMPT_MAX_LENGTH) {
    return tr('promptCase.errI2iPromptTooLong', { max: IMG2IMG_PROMPT_MAX_LENGTH });
  }
  return null;
}

export function buildImg2ImgPromptCasePayload(form) {
  return {
    name: String(form?.name || '').trim(),
    prompt: String(form?.prompt || '').trim(),
    caseScope: form?.caseScope === 'team' ? 'team' : 'personal',
  };
}

/**
 * 万能改图模板 · 新建 / 编辑表单
 */
export default function Img2ImgPromptCaseFormModal({
  isOpen,
  mode = 'create',
  /** 可选：工作台顶栏模式（不传则自动跟随顶栏）。弹窗内不可切换归属 */
  workspaceMode,
  caseItem = null,
  defaults = null,
  canAccessTeamPresets = false,
  saving = false,
  onClose,
  onSubmit,
  /** 居中弹窗：工作流节点参数面板内使用（功能页默认仍贴左侧参数栏滑入） */
  centered = false,
}) {
  const isEdit = mode === 'edit';
  const { t } = useTranslation('workbench');
  const {
    mounted,
    exiting,
    requestClose,
    handlePanelAnimationEnd,
  } = useHistoryPanelTransition(isOpen, onClose, {
    baseClassName: 't2i-case-form-modal-layer',
    deferContent: false,
    closeNotifiesParent: true,
  });
  const dockLeft = useStudioSidebarDockLeft(mounted && !centered);
  const workspaceModeFromHook = useIpeWorkspaceMode();
  const lockedScope = resolveLockedTitlePresetScope(
    workspaceMode || workspaceModeFromHook,
    canAccessTeamPresets,
  );
  const [form, setForm] = useState(() => emptyForm(lockedScope, defaults || {}));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(formFromCase(caseItem, lockedScope));
    } else {
      setForm(emptyForm(lockedScope, defaults || {}));
    }
    setError('');
  }, [isOpen, isEdit, caseItem, lockedScope, defaults]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, saving, requestClose]);

  const promptLen = String(form.prompt || '').length;
  const canSubmit = useMemo(() => {
    return Boolean(String(form.name || '').trim() && String(form.prompt || '').trim()) && !saving;
  }, [form.name, form.prompt, saving]);

  if (!mounted) return null;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const msg = validateImg2ImgPromptCaseForm(form, t);
    if (msg) {
      setError(msg);
      return;
    }
    const payload = buildImg2ImgPromptCasePayload(form);
    await onSubmit?.(payload, { mode, caseItem });
  };

  const safeClose = () => {
    if (!saving) requestClose();
  };

  return createPortal(
    <div
      className={`funding-modal funding-modal--top t2i-case-form-modal-layer${centered ? ' ipe-case-modal--centered' : ''}${exiting ? ' is-closing' : ' is-opening'}`}
      style={{ '--t2i-case-library-dock-left': `${dockLeft}px` }}
    >
      <div
        className="funding-modal__backdrop"
        role="presentation"
        onClick={safeClose}
      />
      <div
        className="funding-modal__panel funding-modal__panel--unified funding-modal__panel--t2i-case-form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="i2i-case-form-title"
        onAnimationEnd={handlePanelAnimationEnd}
      >
        <header className="funding-modal__header t2i-case-form__header">
          <div className="t2i-case-form__header-text">
            <h3 id="i2i-case-form-title">{isEdit ? t('promptCase.editTemplate') : t('promptCase.newTemplate')}</h3>
            <p className="t2i-case-form__subtitle">
              {isEdit ? t('promptCase.syncHint') : t('promptCase.i2iSubtitle')}
            </p>
          </div>
        </header>

        <form className="funding-modal__body t2i-case-form__body" onSubmit={handleSubmit}>
          <section className="t2i-case-form__section">
            <label className="t2i-case-form__label" htmlFor="i2i-case-name">
              {t('promptCase.name')}<span className="t2i-case-form__req" style={{ color: '#f87171' }}>*</span>
            </label>
            <input
              id="i2i-case-name"
              type="text"
              className="t2i-case-form__input"
              value={form.name}
              maxLength={128}
              disabled={saving}
              onChange={(e) => setField('name', e.target.value)}
              placeholder={t('promptCase.i2iNamePh')}
            />
          </section>

          <section className="t2i-case-form__section">
            <div className="t2i-case-form__label-row">
              <label className="t2i-case-form__label" htmlFor="i2i-case-prompt">
                {t('promptCase.i2iPrompt')}<span className="t2i-case-form__req" style={{ color: '#f87171' }}>*</span>
              </label>
              <span className={`t2i-case-form__count${promptLen >= IMG2IMG_PROMPT_MAX_LENGTH ? ' is-limit' : ''}`}>
                {promptLen}/{IMG2IMG_PROMPT_MAX_LENGTH}
              </span>
            </div>
            <textarea
              id="i2i-case-prompt"
              className="t2i-case-form__textarea"
              value={form.prompt}
              maxLength={IMG2IMG_PROMPT_MAX_LENGTH}
              disabled={saving}
              rows={5}
              onChange={(e) => setField('prompt', e.target.value)}
              placeholder={t('promptCase.i2iPromptPh')}
            />
          </section>

          {error ? (
            <p className="t2i-case-form__error" role="alert">{error}</p>
          ) : null}

          <footer className="t2i-case-form__footer">
            <button
              type="button"
              className="funding-button funding-button--ghost t2i-case-form__btn"
              onClick={onClose}
              disabled={saving}
            >
              {t('promptCase.cancel')}
            </button>
            <button
              type="submit"
              className="funding-button funding-button--accent t2i-case-form__btn t2i-case-form__btn--primary"
              disabled={!canSubmit}
            >
              {saving
                ? t('promptCase.saving')
                : (isEdit ? t('promptCase.saveChanges') : t('promptCase.createTemplate'))}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body,
  );
}
